import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import type { FunctionComponent } from "react"
import { useCallback, useEffect, useRef, useState } from "react"
import { Alert, Badge, Button, Form, Spinner } from "react-bootstrap"
import approvalService from "src/components/approvals/ApprovalService"
import ActionApproval from "src/components/approvals/crud/ActionApproval"
import GetApprovalAuditTrail from "src/components/approvals/crud/GetApprovalAuditTrail"
import GetApprovalStatus from "src/components/approvals/crud/GetApprovalStatus"
import Loader from "src/components/common/Loader"
import snackBar from "src/utilities/snackBar/SnackBar"
import GetBatchUpdateRequestPayload from "../api/batchUpdateRequest"
import {
	APPROVAL_COMMENT_MAX_LENGTH,
	APPROVE_ACTION_NAME,
	APPROVED_STATUS_NAME,
	getActionTypeId,
	getEmployeeName,
	PENDING_STATUS_NAME,
	REJECT_ACTION_NAME,
	REJECTED_STATUS_NAME,
	resolveBatchUpdateRecordTypeId,
	toPastTense,
} from "../constants/BatchUpdateApprovalConfig"
import type {
	I_ApproverActionsProps,
	I_AuditTrailProps,
	I_BatchUpdateApprovalProps,
	I_BatchUpdateRequestState,
	I_RequestBannerProps,
} from "../types"
import "./BatchUpdateApprovalDecoupler.css"

dayjs.extend(utc)

// Server datetimes are UTC but carry no offset designator, so Date would parse them as local
// and render the UTC clock time as if it were the viewer's.
const formatDate = (value: string | null): string =>
	value ? dayjs.utc(value).local().toDate().toLocaleString() : "—"

function RequestBanner({ payload, status, isApprover }: I_RequestBannerProps) {
	if (status === REJECTED_STATUS_NAME) {
		return (
			<Alert variant="secondary" className="mb-0">
				<strong>Denied.</strong> No responses were changed. A denied request
				cannot be reopened — it needs a fresh submission.
			</Alert>
		)
	}

	if (payload.ExecutedDate !== null) {
		return (
			<Alert variant="success" className="mb-0">
				<strong>Approved.</strong> This update ran on{" "}
				{formatDate(payload.ExecutedDate)}. Reopening this notification changes
				nothing.
			</Alert>
		)
	}

	const scope = (
		<strong>
			This runs {payload.UpdateDisplayName} on {payload.ResponseCount} billing
			response(s).
		</strong>
	)

	if (status === APPROVED_STATUS_NAME) {
		return (
			<Alert variant="warning" className="mb-0">
				{scope} It was approved but has not run. Check with a developer before
				resubmitting.
			</Alert>
		)
	}

	if (isApprover) {
		return (
			<Alert variant="danger" className="mb-0">
				{scope} Approving runs the update immediately, attributed to the
				requester. There is no undo.
			</Alert>
		)
	}

	return (
		<Alert variant="warning" className="mb-0">
			{scope} It is awaiting a decision from its assigned approver.
		</Alert>
	)
}

function ApproverActions({
	payload,
	comment,
	busy,
	handlers,
}: I_ApproverActionsProps) {
	return (
		<>
			<Form.Group className="mb-0">
				<Form.Label>Comment — required to deny</Form.Label>
				<Form.Control
					as="textarea"
					rows={3}
					maxLength={APPROVAL_COMMENT_MAX_LENGTH}
					value={comment}
					disabled={busy}
					placeholder="Explain the refusal. The requester receives this verbatim."
					onChange={(e) => handlers.onCommentChange(e.target.value)}
				/>
				<Form.Text muted>
					{comment.length} / {APPROVAL_COMMENT_MAX_LENGTH}
				</Form.Text>
			</Form.Group>

			<div className="BatchUpdateApprovalDecouplerActions">
				<Button variant="danger" disabled={busy} onClick={handlers.onApprove}>
					Approve &amp; run on {payload.ResponseCount} response(s)
				</Button>
				<Button
					variant="outline-secondary"
					disabled={busy || comment.trim() === ""}
					onClick={handlers.onDeny}
				>
					{busy ? "Working..." : "Deny"}
				</Button>
				{busy ? <Spinner animation="border" size="sm" /> : null}
			</div>
		</>
	)
}

function AuditTrail({ audit }: I_AuditTrailProps) {
	if (audit.length === 0) return null

	return (
		<Form.Group className="mb-0">
			<Form.Label>Audit</Form.Label>
			<div className="BatchUpdateApprovalDecouplerAudit">
				{audit.map((entry) => (
					<div key={entry.ApprovalActionId}>
						<strong>{formatDate(entry.ActionedDateUTC ?? null)}</strong> &nbsp;
						{toPastTense(entry.Action)} by {getEmployeeName(entry.ActionedBy)}
						{entry.Comments ? ` — ${entry.Comments}` : ""}
					</div>
				))}
			</div>
		</Form.Group>
	)
}

const BatchUpdateApprovalDecoupler: FunctionComponent<
	I_BatchUpdateApprovalProps
> = ({ batchUpdateRequestId, userId }) => {
	const [data, setData] = useState<I_BatchUpdateRequestState | null>(null)
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState<string | null>(null)

	const [comment, setComment] = useState("")
	const [confirmingApprove, setConfirmingApprove] = useState(false)
	const [busy, setBusy] = useState(false)

	const isFirst = useRef(true)

	const load = useCallback(async () => {
		setLoading(true)
		setError(null)

		try {
			if (!approvalService.initialized) {
				await approvalService.init()
			}

			const recordTypeId = await resolveBatchUpdateRecordTypeId()

			if (recordTypeId === null) {
				setError("Batch update approval is not configured on this environment.")
				return
			}

			const [payload, steps, audit] = await Promise.all([
				GetBatchUpdateRequestPayload(batchUpdateRequestId),
				GetApprovalStatus(recordTypeId, batchUpdateRequestId),
				GetApprovalAuditTrail(recordTypeId, batchUpdateRequestId),
			])

			if (!payload) {
				setError("This batch update request could not be loaded.")
				return
			}

			// Every request has at least one step, so an empty list means the call failed.
			if (steps.length === 0) {
				setError("The approval status for this request could not be loaded.")
				return
			}

			setData({ payload, steps, audit })
		} catch (loadError) {
			console.error("Failed to load the batch update approval:", loadError)
			setError("This batch update request could not be loaded.")
		} finally {
			setLoading(false)
		}
	}, [batchUpdateRequestId])

	useEffect(() => {
		if (!isFirst.current) return
		isFirst.current = false
		load()
	}, [load])

	if (loading) return <Loader title="Loading batch update request" />

	if (error || !data) {
		return (
			<Alert variant="danger">
				{error ?? "This batch update request could not be loaded."}
			</Alert>
		)
	}

	const { payload, steps, audit } = data

	// A pending step means the workflow is still open; otherwise the highest step order
	// carries the outcome.
	const pendingStep =
		steps.find((s) => s.Status === PENDING_STATUS_NAME) ?? null
	const latestStep =
		[...steps].sort((a, b) => b.StepOrder - a.StepOrder)[0] ?? null
	const currentStatus = (pendingStep ?? latestStep)?.Status ?? null

	const actionableStep =
		pendingStep && pendingStep.ApproverUserId === userId ? pendingStep : null
	const isApprover = actionableStep !== null
	const showingAll = payload.ResponseDetailIds.length >= payload.ResponseCount

	const submitAction = async (actionName: string) => {
		if (!actionableStep) return

		const actionTypeId = getActionTypeId(actionName)

		if (actionTypeId === -1) {
			snackBar({
				type: "error",
				text: `Could not resolve the ${actionName} action type.`,
				timeout: 4000,
			})
			return
		}

		setBusy(true)

		try {
			const success = await ActionApproval({
				ApprovalStepId: actionableStep.ApprovalStepId,
				ActionedBy: userId,
				ApprovalActionTypeId: actionTypeId,
				Comments: comment.trim() === "" ? null : comment.trim(),
			})

			if (success) {
				snackBar({
					type: "success",
					text:
						actionName === APPROVE_ACTION_NAME
							? "Batch update approved."
							: "Batch update denied.",
					timeout: 4000,
				})
				setConfirmingApprove(false)
				setComment("")
				await load()
			}
		} finally {
			setBusy(false)
		}
	}

	// The notification is one click from a live billing change, so approve confirms.
	if (confirmingApprove) {
		return (
			<div className="BatchUpdateApprovalDecoupler">
				<Alert variant="danger">
					<Alert.Heading as="h5">
						Run {payload.UpdateDisplayName} on {payload.ResponseCount} billing
						response(s)?
					</Alert.Heading>
					<p className="mb-0">
						This cannot be undone, and {payload.RequestedByName} will be
						notified that you approved it.
					</p>
				</Alert>

				<div className="BatchUpdateApprovalDecouplerActions">
					<Button
						variant="danger"
						disabled={busy}
						onClick={() => submitAction(APPROVE_ACTION_NAME)}
					>
						{busy
							? "Running..."
							: `Yes, run on ${payload.ResponseCount} response(s)`}
					</Button>
					<Button
						variant="outline-secondary"
						disabled={busy}
						onClick={() => setConfirmingApprove(false)}
					>
						Cancel
					</Button>
				</div>
			</div>
		)
	}

	return (
		<div className="BatchUpdateApprovalDecoupler">
			<RequestBanner
				payload={payload}
				status={currentStatus}
				isApprover={isApprover}
			/>

			<div className="BatchUpdateApprovalDecouplerMeta">
				<div>
					<span className="BatchUpdateApprovalDecouplerMetaKey">
						Requested by
					</span>
					<span className="BatchUpdateApprovalDecouplerMetaValue">
						{payload.RequestedByName}
					</span>
				</div>
				<div>
					<span className="BatchUpdateApprovalDecouplerMetaKey">Submitted</span>
					<span className="BatchUpdateApprovalDecouplerMetaValue">
						{formatDate(payload.RequestedDate)}
					</span>
				</div>
				<div>
					<span className="BatchUpdateApprovalDecouplerMetaKey">Update</span>
					<span className="BatchUpdateApprovalDecouplerMetaValue">
						{payload.UpdateDisplayName}
					</span>
				</div>
				<div>
					<span className="BatchUpdateApprovalDecouplerMetaKey">Responses</span>
					<span className="BatchUpdateApprovalDecouplerMetaValue">
						{payload.ResponseCount}
					</span>
				</div>
			</div>

			{payload.UpdateInstructions ? (
				<Alert variant="light" className="mb-0">
					{payload.UpdateInstructions}
				</Alert>
			) : null}

			{payload.Comments ? (
				<Form.Group className="mb-0">
					<Form.Label>Requester&rsquo;s reason</Form.Label>
					<div>
						<em>{payload.Comments}</em>
					</div>
				</Form.Group>
			) : null}

			{payload.SecondaryField ? (
				<Form.Group className="mb-0">
					<Form.Label>
						{payload.SecondaryFieldLabel ?? "Secondary field"}
					</Form.Label>
					<div>{payload.SecondaryField}</div>
				</Form.Group>
			) : null}

			<Form.Group className="mb-0">
				<Form.Label>
					What this affects{" "}
					<Badge variant="secondary">
						{showingAll
							? `${payload.ResponseCount} rows`
							: `showing ${payload.ResponseDetailIds.length} of ${payload.ResponseCount}`}
					</Badge>
				</Form.Label>
				<div className="BatchUpdateApprovalDecouplerIds">
					{payload.ResponseDetailIds.map((id) => (
						<span key={id}>{id}</span>
					))}
				</div>
			</Form.Group>

			{isApprover ? (
				<ApproverActions
					payload={payload}
					comment={comment}
					busy={busy}
					handlers={{
						onCommentChange: setComment,
						onApprove: () => setConfirmingApprove(true),
						onDeny: () => submitAction(REJECT_ACTION_NAME),
					}}
				/>
			) : null}

			<AuditTrail audit={audit} />
		</div>
	)
}

export default BatchUpdateApprovalDecoupler
